import moment from 'moment'
import React from 'react';
import {
  Link
} from 'react-router-dom';
import socketIOClient from "socket.io-client";
import LiveUpdates from './LiveUpdates';
import SeenAtUpdates from './SeenAtUpdates';
import { sortByLiveRank } from './liveRanking';

// In dev (REACT_APP_FAKE_NOW=1) gebruiken we de laatste read als 'nu' i.p.v. de echte klok,
// zodat de simulator/replay (met vorig-jaars timestamps) testbaar blijft. In productie staat
// dit uit -> echte tijd. CRA bakt REACT_APP_* in bij de build.
const USE_FAKE_NOW = process.env.REACT_APP_FAKE_NOW === '1';

// Rode Lantaarn — drempels (min) waarboven 'stilte sinds dit stadium' op gestopt wijst.
// = veld-maximum vorig jaar + kleine marge (uit water 21m, fiets 138m, T2 18m, lopen 115m).
const SILENCE_MAX_MIN = { stoppedSwimming: 25, startedBiking: 150, stoppedBiking: 25, startedRunning: 120 };

// leesbaar label van het laatste punt waar iemand gezien werd
const STAGE_LABEL = {
  stoppedSwimming: 'het uit het water komen',
  startedBiking: 'de start op de fiets',
  stoppedBiking: 'het van de fiets komen',
  startedRunning: 'de start van het lopen'
};

// Cohort-boxen waar mensen uit dezelfde wave relatief samen overkomen. We kijken van het
// verste punt (waar de speler nu is) naar het vroegste; het venster is breder voor latere
// boxen omdat de wave daar meer uitgewaaierd is (zwemmen ~8m, lopen ~32m binnen de wave).
const COHORT_BOXES = [
  { key: 'startedRunning',  label: 'aan het lopen ging', win: 20, kind: 'run' },
  { key: 'stoppedBiking',   label: 'van de fiets kwam',  win: 20, kind: 'bike' },
  { key: 'startedBiking',   label: 'op de fiets vertrok', win: 10, kind: 'bike' },
  { key: 'stoppedSwimming', label: 'uit het water kwam', win: 6,  kind: 'swim' }
];

// Wave-cohort: zoek de groep waarmee de speler over een eerdere box kwam (zelfde wave + samen
// over die box). Is die groep al ruim binnen en deze speler niet -> vermoedelijk gestopt.
function cohortReason(player, allPlayers, now) {
  var splits = player.splits || {};
  for (var box of COHORT_BOXES) {
    var t = splits[box.key];
    if (!t) continue; // deze box nog niet gepasseerd
    var peers = allPlayers.filter(p => p.id !== player.id && p.wave === player.wave
      && p.splits && p.splits[box.key] && Math.abs(p.splits[box.key] - t) <= box.win * 60000);
    if (peers.length < 5) continue;
    var finished = peers.filter(p => p.event === 'finished');
    if (finished.length / peers.length < 0.9) continue;
    var lastPeerFinish = Math.max.apply(null, finished.map(p => p.lastUpdateTime));
    // pas taggen als die groep al rúim binnen is (15 min marge): een trage finisher
    // wiens groep net vóór 'nu' binnenkwam mag je niet als gestopt bestempelen.
    if (now - lastPeerFinish > 15 * 60000) {
      var doneMin = Math.floor((now - lastPeerFinish) / 60000);
      return {
        text: 'iedereen die samen met hem ' + box.label + ' (wave ' + player.wave + ') is al ' + doneMin + ' min binnen',
        kind: box.kind
      };
    }
  }
  return null;
}

// Bepaalt WAAROM een speler vermoedelijk gestopt is. Geeft { text, kind } (voor de kleur), of null.
// kind: 'silence' (te lang niet gezien) of 'swim'/'bike'/'run' (wave-cohort op die box).
function computeStopReason(player, allPlayers, now) {
  if (player.event === 'finished') return null;

  // tier 1 — al langer niet gezien dan wie dan ook over dit stuk deed
  var silenceMin = Math.floor((now - player.lastUpdateTime) / 60000);
  var max = SILENCE_MAX_MIN[player.event];
  if (max != null && silenceMin > max) {
    return {
      text: silenceMin + ' min niet meer gezien sinds ' + (STAGE_LABEL[player.event] || player.event)
        + ' — niemand deed daar langer dan ' + max + ' min over',
      kind: 'silence'
    };
  }

  // tier 2 — wave-cohort over alle convergentie-boxen
  return cohortReason(player, allPlayers, now);
}

class LivePositions extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      players: [],
      views: []
    };

    this.socket = null;
  }

  componentDidMount() {
    this.fetchAllPlayerUpdates();
    this.fetchViews();
    this.listenToSocket();
  }

  componentWillUnmount() {
    if(this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  listenToSocket() {
    if(this.socket) return; // prevent multiple connections
    this.socket = socketIOClient({path: "/socketio/livescores" });

    // some debugging statements concerning socket.io
    this.socket.on('reconnecting', seconds => {
      console.log('reconnecting in ' + seconds + ' seconds');
    });

    this.socket.on('reconnect', () => {
      console.log('reconnected');
    });

    this.socket.on('reconnect_failed', () => {
      console.log('failed to reconnect');
    });

    this.socket.on('connect', () => {
      console.log('socket connected');
    });

    this.socket.on("playerUpdate", playerUpdate => {
      this.onPlayerUpdate(playerUpdate);
    });
  }

  onPlayerUpdate(playerUpdate) {
    // console.log(playerUpdate);

    var players = this.state.players;
    var index = players.findIndex(player => player.id === playerUpdate.id);
    if(index === -1) {
      players.push(playerUpdate);
    }else{
      players[index] = playerUpdate;
    }
    this.setState({players: players});
  }

  async fetchViews() {
    var games = (await (await fetch(`/api/results/games`)).json()).games;

    var views = [];

    var namePrefix = '';

    for(var game of games) {
      if(game.id == 2) namePrefix = `${game.name}: `;

      views.push({
        name: `${game.name}`,
        game: game,
        categoryExclusionRegex: /trio/i
      });

      views.push({
        name: `${namePrefix}Mannen`,
        game: game,
        sex: 'male',
        categoryExclusionRegex: /trio/i
      });

      views.push({
        name: `${namePrefix}Vrouwen`,
        game: game,
        sex: 'female',
        categoryExclusionRegex: /trio/i
      });

      var categories = (await (await fetch(`/api/results/games/${game.id}/categories`)).json()).categories;
      for(var category of categories) {
        views.push({
          name: `${namePrefix}${category}`,
          game: game,
          category: category
        });
      }

      views.push({
        name: `Rode Lantaarn`,
        orderByLastUpdateTime: true
      });
    }

    this.setState({views: views})
  }

  async fetchAllPlayerUpdates() {
    var response = await fetch(`/api/livescores/playerupdates`);
    var data = await response.json();
    this.setState({players: data.playerUpdates})
  }

  renderNavigation() {
    var navs = this.state.views.map((view, index) => {
      return (
        <li className="nav-item" key={view.name}>
          <Link to={`/positions/${index}`} className={`nav-link ${this.props.match.params.viewIndex == index?'active':''}`}>{view.name}</Link>
        </li>
      );
    });

    return (
      <ul className="nav md-tabs nav-justified primary-color lighten-2 mx-0 mb-0 mt-0 liveheader">
        {navs}
      </ul>
    )
  }

  render() {
    document.title = "Live Positions";
    if(this.state.views.length == 0) return;

    var view = this.state.views[this.props.match.params.viewIndex];
    if(!view) return "";

    document.title = `Live Positions - ${view.name}`;

    var players = this.state.players;
    // filter based on view:
    players = players.filter(player => {
      if(view.game && player.gameId != view.game.id) return false;
      if(view.sex && player.sex != view.sex) return false;
      if(view.categoryExclusionRegex && player.category.match(view.categoryExclusionRegex)) return false;
      if(view.category && player.category != view.category) return false;
      return true;
    });

    if(!view.orderByLastUpdateTime) {

      // voorlopige live-rang (gedeelde logica met Live.jsx): op tijd + voortgang
      players = sortByLiveRank(players);

      return(
        <div>
          {this.renderNavigation()}
          <LiveUpdates players={players} title={view.name} timeTitle="Tijd" compact />
        </div>
      );

    }else{
      // de rode lantaarn:


      players = players.sort((p1, p2) => {
        return p2.lastUpdateTime - p1.lastUpdateTime;
      });


      var finishedPlayers = players.filter(player => player.event == 'finished').slice(0, 1); // show the last one arrived
      var unfinishedPlayers = players.filter(player =>  player.event != 'finished');

      // it's a bit of black magic to choose in which order they should appear

      // the point is that the higher in the ranking, the best chances of still arriving in that order
      unfinishedPlayers = unfinishedPlayers.sort((p1, p2) => {
        return p2.lastUpdateTime - p1.lastUpdateTime;
      });

      // // players from the last wave have more chance of arriving:
      // unfinishedPlayers = unfinishedPlayers.sort((p1, p2) => {
      //   return p2.wave - p1.wave;
      // });
      // removed, makes it confusing

      var eventOrder = [
        'stoppedSwimming',
        'startedBiking',
        'stoppedBiking',
        'startedRunning',
        'finished'
      ];

      players = unfinishedPlayers.sort((p1, p2) => {
        var i1 = eventOrder.indexOf(p1.event);
        var i2 = eventOrder.indexOf(p2.event);
        return i2 - i1;
      });




      // 'nu' = echte tijd voor de live race; in dev (REACT_APP_FAKE_NOW=1) de laatste read als
      // 'nu' zodat de simulator/replay (vorig-jaars timestamps) testbaar blijft.
      var now = USE_FAKE_NOW
        ? (this.state.players.reduce((mx, p) => Math.max(mx, p.lastUpdateTime || 0), 0) || Date.now())
        : Date.now();
      // tag waarom iemand vermoedelijk gestopt is (volgorde blijft ongewijzigd)
      unfinishedPlayers.forEach(p => { p.stopReason = computeStopReason(p, this.state.players, now); });

      players = [...finishedPlayers, ...unfinishedPlayers];

      console.log('de laatste', players);

      return(
        <div>
          {this.renderNavigation()}
          <SeenAtUpdates players={players} title={view.name} now={now} />
        </div>
      );

    }
  }
}

export default LivePositions
